import { h, render } from "preact";
import MonogramConfigurator from "./monogram-configurator.jsx";
import { currencyRate, monogramCartItems } from "./monogram-cart.js";
import { availableTypes } from "./monogram-options.js";

const MODAL_ID = "monogram-modal";

// Survives the modal closing, which empties its content and unmounts the
// configurator. Keyed by product id.
const selections = new Map();

function readConfig(id) {
  const script = id && document.getElementById(id);
  if (!script) return null;

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.error("Monogram config could not be parsed", error);
    return null;
  }
}

class MonogramTrigger extends HTMLElement {
  connectedCallback() {
    const config = readConfig(this.dataset.configId);

    if (!config || !availableTypes(config).length) {
      this.hidden = true;
      return;
    }

    this.button = this.querySelector("button");
    this.button?.addEventListener("click", this.open);

    this.productForm = this.closest("product-form");
    this.productForm?.addEventListener(
      "variant:changed",
      this.onVariantChanged,
    );
  }

  disconnectedCallback() {
    this.button?.removeEventListener("click", this.open);
    this.productForm?.removeEventListener(
      "variant:changed",
      this.onVariantChanged,
    );
  }

  // Follows the add to bag button: the monogram adds the product with it.
  onVariantChanged = (event) => {
    const { variant, pendingOption } = event.detail;
    if (this.button) {
      this.button.disabled = !variant?.available || Boolean(pendingOption);
    }
  };

  open = (event) => {
    // Not `.id`, which the form's variant input (name="id") shadows.
    const formId = this.closest("form")?.getAttribute("id") ?? "";

    event.currentTarget.dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: {
          target: MODAL_ID,
          html: `<monogram-configurator data-config-id="${this.dataset.configId}" data-form-id="${formId}"></monogram-configurator>`,
        },
      }),
    );
  };
}

class MonogramConfiguratorElement extends HTMLElement {
  connectedCallback() {
    const config = readConfig(this.dataset.configId);
    if (!config) return;

    this.form = document.getElementById(this.dataset.formId);
    const variantId = Number(this.form?.querySelector('[name="id"]')?.value);
    const variant = config.variants?.find((item) => item.id === variantId);

    render(
      <MonogramConfigurator
        config={config}
        variant={variant}
        initialSelection={selections.get(config.productId)}
        onAdd={(selection) => this.add(config, variant, selection)}
        onSelectionChange={(selection) =>
          selections.set(config.productId, selection)
        }
      />,
      this,
    );
  }

  disconnectedCallback() {
    render(null, this);
  }

  /**
   * Adds the product and its monogram as two linked lines.
   *
   * @returns {Promise} Rejects with the cart's message, if it gave one
   */
  add(config, variant, selection) {
    const properties = {};
    for (const [name, value] of new FormData(this.form ?? undefined)) {
      const key = /^properties\[(.+)\]$/.exec(name)?.[1];
      if (key) properties[key] = value;
    }

    const items =
      variant &&
      monogramCartItems({
        config,
        selection,
        variant,
        properties,
        uuid: crypto.randomUUID(),
        rate: currencyRate(),
      });

    if (!items) return Promise.reject(new Error());

    return new Promise((resolve, reject) => {
      window.addToCart(items, {
        onAfterAdd: () => {
          selections.delete(config.productId);
          document.dispatchEvent(
            new CustomEvent("markup-modal:close", {
              detail: { target: MODAL_ID },
            }),
          );
          document.dispatchEvent(new CustomEvent("mini-cart:open"));
          resolve();
        },
        onError: (response) =>
          reject(new Error(response?.description || response?.message || "")),
      });
    });
  }
}

if (!customElements.get("monogram-trigger")) {
  customElements.define("monogram-trigger", MonogramTrigger);
}

if (!customElements.get("monogram-configurator")) {
  customElements.define("monogram-configurator", MonogramConfiguratorElement);
}
